import { useMemo, useState } from 'react';
import { Page, PageHeader, SectionTitle } from '@/client/components/AppShell';
import { AssignmentEditor, AssignmentRow } from '@/client/components/assignments';
import { AlertIcon, RefreshIcon, SparkIcon } from '@/client/components/icons';
import { Badge, Button, Card, EmptyState, SkeletonList } from '@/client/components/ui';
import { useWorkspace } from '@/client/store';
import { addWeeks, compareDates, formatShort } from '@/domain/dates';
import { nextUpPanels } from '@/domain/generate';
import { computeCoverage } from '@/domain/stats';
import type { Assignment } from '@/shared/types';

/**
 * The weekly working screen: what is coming up, what still needs a name, and
 * who the ranker would pick if someone drops out today.
 */
export function Today() {
  const { data: workspace, isPending, isFetching, refetch } = useWorkspace();
  const [editing, setEditing] = useState<Assignment | null>(null);

  const derived = useMemo(() => {
    if (!workspace) return null;

    const twoWeeks = addWeeks(workspace.today, 2);
    const horizon = addWeeks(workspace.today, workspace.settings.calendarSyncHorizonWeeks);

    const upcoming = workspace.assignments
      .filter(
        (a) =>
          compareDates(a.date, workspace.today) >= 0 &&
          compareDates(a.date, twoWeeks) <= 0 &&
          a.outcome !== 'No Meeting',
      )
      .sort((a, b) => compareDates(a.date, b.date));

    const needsOutcome = workspace.assignments
      .filter(
        (a) =>
          a.outcome === 'Pending' &&
          a.volunteerId &&
          compareDates(a.date, workspace.today) < 0 &&
          compareDates(a.date, addWeeks(workspace.today, -6)) >= 0,
      )
      .sort((a, b) => compareDates(b.date, a.date));

    return {
      upcoming,
      needsOutcome,
      coverage: computeCoverage(workspace.assignments, workspace.today, horizon),
      panels: nextUpPanels(
        workspace.volunteers,
        workspace.assignments,
        workspace.settings,
        workspace.today,
      ),
      horizon,
    };
  }, [workspace]);

  return (
    <>
      <PageHeader
        title="Today"
        subtitle={workspace ? formatShort(workspace.today) : undefined}
        actions={
          <Button
            variant="ghost"
            onClick={() => void refetch()}
            busy={isFetching && !isPending}
            aria-label="Refresh from Google Sheets"
            className="!px-2"
          >
            <RefreshIcon className="size-5" />
          </Button>
        }
      />

      <Page>
        {isPending || !workspace || !derived ? (
          <SkeletonList rows={5} />
        ) : (
          <>
            {derived.coverage.unassigned > 0 ? (
              <Card className="flex items-start gap-3 border-caution/40 bg-caution-soft px-4 py-3">
                <AlertIcon className="mt-0.5 size-5 shrink-0 text-caution" />
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {derived.coverage.unassigned}{' '}
                    {derived.coverage.unassigned === 1 ? 'date needs' : 'dates need'} a volunteer
                  </p>
                  <p className="mt-0.5 text-xs text-ink-muted">
                    In the next {workspace.settings.calendarSyncHorizonWeeks} weeks:{' '}
                    {derived.coverage.gaps.slice(0, 4).map(formatShort).join(', ')}
                    {derived.coverage.gaps.length > 4
                      ? ` and ${derived.coverage.gaps.length - 4} more`
                      : ''}
                    .
                  </p>
                </div>
              </Card>
            ) : null}

            {derived.needsOutcome.length > 0 ? (
              <section className="flex flex-col gap-2">
                <SectionTitle
                  title="Needs an outcome"
                  hint="Recording these keeps the fairness ranking honest."
                />
                <ul className="flex flex-col gap-2">
                  {derived.needsOutcome.slice(0, 5).map((assignment) => (
                    <AssignmentRow
                      key={`${assignment.date}|${assignment.riderId}`}
                      assignment={assignment}
                      workspace={workspace}
                      onOpen={setEditing}
                    />
                  ))}
                </ul>
              </section>
            ) : null}

            <section className="flex flex-col gap-2">
              <SectionTitle title="Next two weeks" />
              {derived.upcoming.length === 0 ? (
                <Card>
                  <EmptyState
                    title="Nothing scheduled yet"
                    description="Head to Schedule and run Generate to fill out the calendar."
                  />
                </Card>
              ) : (
                <ul className="flex flex-col gap-2">
                  {derived.upcoming.map((assignment) => (
                    <AssignmentRow
                      key={`${assignment.date}|${assignment.riderId}`}
                      assignment={assignment}
                      workspace={workspace}
                      onOpen={setEditing}
                    />
                  ))}
                </ul>
              )}
            </section>

            <section className="flex flex-col gap-2">
              <SectionTitle
                title="Next up"
                hint="Who the ranker would pick, if you need a replacement right now."
              />
              <div className="grid gap-2 sm:grid-cols-2">
                {derived.panels.map((panel) => (
                  <Card key={panel.dayType} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium">{panel.dayType}</p>
                      {panel.date ? (
                        <span className="text-xs text-ink-faint">{formatShort(panel.date)}</span>
                      ) : null}
                    </div>

                    {panel.candidates.length === 0 ? (
                      <p className="mt-2 text-xs text-ink-faint">
                        Nobody is available for {panel.dayType}s.
                      </p>
                    ) : (
                      <ol className="mt-2 flex flex-col gap-1.5">
                        {panel.candidates.map((candidate, index) => (
                          <li
                            key={candidate.volunteerId}
                            className="flex items-center justify-between gap-2 text-sm"
                          >
                            <span className="flex min-w-0 items-center gap-1.5">
                              {index === 0 ? (
                                <SparkIcon className="size-4 shrink-0 text-brand" />
                              ) : (
                                <span className="w-4 shrink-0 text-center text-xs text-ink-faint">
                                  {index + 1}
                                </span>
                              )}
                              <span className="truncate">{candidate.name}</span>
                            </span>
                            <Badge>{candidate.credit} in window</Badge>
                          </li>
                        ))}
                      </ol>
                    )}
                  </Card>
                ))}
              </div>
            </section>

            <p className="px-1 text-xs text-ink-faint">
              {derived.coverage.confirmed} of {derived.coverage.total} rides confirmed through{' '}
              {formatShort(derived.horizon)} ·{' '}
              {workspace.volunteers.filter((v) => v.status === 'Active').length} active volunteers
            </p>
          </>
        )}
      </Page>

      {workspace ? (
        <AssignmentEditor
          assignment={editing}
          workspace={workspace}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}
